import { FilmCategory } from '@vista-digital/ocapi-types/v1'
import { TaxMode, TicketingMode } from '@vista-digital/occ-domain'
import {
    BookingFeeMode,
    OrderConfig,
    TicketRedemptionCodeCategory,
    TicketRedemptionCodeType,
    TicketVoucherTypePin,
} from '@vista-digital/occ-domain'
import { NestedPartial } from '@vista-digital/utilities'
import { OccAccessibilityText, OccAssets, OccFilmConfig, OccFilterConfig, OccMemberConfig } from '@vista-digital/occ-ui'
import { defaultOccAccessibilityText } from '@vista-digital/occ-ui/src/lib/context/occ/types/accessibility-text'
import { defaultOccAssets } from '@vista-digital/occ-ui/src/lib/context/occ/types/assets'
import { OccFormFieldValidationConfig } from '@vista-digital/occ-ui/src/lib/context/occ/types/config/form/form-field-validation'
import { OccLocaleConfig } from '@vista-digital/occ-ui/src/lib/context/occ/types/config/locale/locale'
import { defaultOccMemberConfig } from '@vista-digital/occ-ui/src/lib/context/occ/types/config/member/member'
import { OccDisplayText } from '@vista-digital/occ-ui/src/lib/context/occ/types/display-text/display-text'
import { defaultOccPriceDisplayText } from '@vista-digital/occ-ui/src/lib/context/occ/types/display-text/price'
import { defaultOccSubscriptionDisplayText } from '@vista-digital/occ-ui/src/lib/context/occ/types/display-text/subscription'
import { FieldFormatType } from '@vista-digital/occ-ui/src/lib/types/form/field-format-type'
import { OccContextPropsApi } from '.'
import { cmsToOccDisplayText } from '../../feComponents/displayText/DisplayText'
import { defaultOccFormDisplayText } from '@vista-digital/occ-ui'
import { defaultOccOrderDisplayText } from '@vista-digital/occ-ui'

// DOMAIN

export function getOccContextDisplayText(props: OccContextPropsApi): OccDisplayText {
    return {
        film: {
            advanceBookingLabel: props.advanceBookingLabel,
            comingSoonLabel: props.comingSoonLabel,
            nowShowingLabel: props.nowShowingLabel,
            preBookNowLabel: props.preBookNowLabel,
            sponsoredLabel: props.sponsoredLabel,
            runtimeFormat: props.filmRuntimeFormat,
            addToWatchlistButtonLabel: props.addToWatchlistButtonLabel,
            removeFromWatchlistButtonLabel: props.removeFromWatchlistButtonLabel,
            failedToAddFilmToWatchlistMessage: props.failedToAddFilmToWatchlistMessage,
            failedToAddFilmToWatchlistTitle: props.failedToAddFilmToWatchlistTitle,
            failedToRemoveFilmFromWatchlistMessage: props.failedToRemoveFilmFromWatchlistMessage,
            failedToRemoveFilmFromWatchlistTitle: props.failedToRemoveFilmFromWatchlistTitle,
        },
        seat: {
            // Maybe in the CMS, probably not
            labelFormat: '${rowLabel}-${seatLabel}',
            groupFormat: '${firstSeatLabel} a ${lastSeatLabel}',
            groupSeparator: ', ',
            placeholderLabel: {
                text: '— ',
                className: 'v-seat-summary-placeholder-label',
            },
            areaNameSeparator: ', ',
        },
        form: {
            ...defaultOccFormDisplayText,
            fieldOptionalSuffix: props.formFieldOptionalSuffix,
            passwordRuleCheckerHelpText: props.passwordRuleCheckerHelpText,
        },
        order: {
            ...defaultOccOrderDisplayText,
            bookingFeeLabel: cmsToOccDisplayText(props.bookingFeeLabel),
            includedBookingFeeLabel: cmsToOccDisplayText(props.includedBookingFeeLabel),
            itemLimitReachedMessage: props.itemLimitReachedMessage,
            inProgressItemLimitReachedMessage: props.inProgressItemLimitReachedMessage,
            estimatedTaxLabel: props.estimatedTaxLabel,
            inProgressItemRedemptionLimitReachedMessage: props.inProgressItemRedemptionLimitReachedMessage,
            subtotalCostExcludingBookingFeeLabel: props.subtotalCostExcludingBookingFeeLabel,
            subtotalCostIncludingBookingFeeLabel: cmsToOccDisplayText(props.subtotalCostIncludingBookingFeeLabel),
            taxLabel: props.taxLabel,
            totalCostLabel: props.totalCostLabel,
            totalSavingsLabel: props.totalSavingsLabel,
        },
        showtime: {
            soldOutLabel: props.soldOutLabel,
        },
        filter: {
            allOptionLabel: props.allOptionLabel,
            nonRecurringSubscriptionBillingPeriodLabel: props.nonRecurringSubscriptionBillingPeriodLabel,
            oneMonthSubscriptionBillingPeriodLabel: props.oneMonthSubscriptionBillingPeriodLabel,
            recurringSubscriptionBillingPeriodLabel: props.recurringSubscriptionBillingPeriodLabel,
            subscriptionBillingPeriodLabel: props.subscriptionBillingPeriodLabel,
        },
        member: {
            rewardExclusiveLabel: props.loyaltyRewardExclusiveLabel,
            defaultBalanceTypeName: props.defaultBalanceTypeName,
            defaultBalanceTypeUnitName: props.defaultBalanceTypeUnitName,
            friendCodeFormat: props.friendCodeFormat,
            memberBalanceFormat: props.memberBalanceFormat,
        },
        price: defaultOccPriceDisplayText, // TODO:
        subscription: defaultOccSubscriptionDisplayText,

        cinemaChainName: props.cinemaChainName,
        componentErrorTitle: props.componentErrorTitle,
        noSearchResultsFoundMessage: props.noSearchResultsFoundMessage,
    }
}

export function getTicketRedemptionCodeTypes(): NestedPartial<TicketRedemptionCodeType>[] {
    return [
        {
            id: '1',
            category: TicketRedemptionCodeCategory.Voucher,
            label: 'Código promocional',
            iconName: 'voucher',
            pin: TicketVoucherTypePin.None,
            codeFormat: undefined,
        },
        {
            id: '2',
            category: TicketRedemptionCodeCategory.ThirdPartyMemberCard,
            label: 'La Vanguardia',
            iconName: 'voucher',
            thirdPartyMemberSchemeId: 'LaVanguardia',
            codeFormat: {
                partialFormat: '^6059\\d{0,12}$',
                fullFormat: '^6059\\d{12}$',
                prefixLength: 4,
            },
        },
    ]
}

// CONFIG
export function getOccLocale(props: OccContextPropsApi): NestedPartial<OccLocaleConfig> {
    return {
        currency: {
            symbol: props.symbol,
            decimalPrecision: props.decimalPrecision,
            decimalSeparator: props.decimalSeparator,
            format: props.format,
            nonNumberPlaceholder: props.nonNumberPlaceholder,
            thousandsSeparator: props.thousandsSeparator,
        },
        date: {
            formats: {
                dateDefault: props.dateDefault,
                dateLong: props.dateLong,
                dateShort: props.dateShort,
                iso: props.iso,
                timeDefault: props.timeDefault,
                timeLong: props.timeLong,
                timeShort: props.timeShort,
            },
            startWeekOnSunday: props.startWeekOnSunday,
        },
        // TODO: number
        // TODO: personName
    }
}

export function getOrderConfig(props: OccContextPropsApi): OrderConfig {
    return {
        admittingDuration: props.admittingDuration,
        bookingFeeMode: props.bookingFeeExclusive ? BookingFeeMode.Exclusive : BookingFeeMode.Inclusive,
        isBookingFeeApplicableToUnpaidBookings: props.isBookingFeeApplicableToUnpaidBookings,
        maximumTicketsPerOrder: props.maximumAllowedTicketsInAnOrder,
        ticketingMode: props.isSeatFirstOrdering ? TicketingMode.SeatFirst : TicketingMode.TicketFirst,
        unpaidBookingCutoffTime: props.unpaidBookingCutoffTime,
        taxMode: props.taxModeExclusive ? TaxMode.Exclusive : TaxMode.Inclusive,
        maximumItemsPerOrder: props.maximumItemsPerOrder,
        maximumModifiersPerItem: props.maximumModifiersPerItem,
        redemptionExpiringSoonDurationInDays: props.redemptionExpiringSoonDurationInDays,
        tip: {
            isEnabled: false,
            includeSundryItems: false
        }
    }
}

export function getFormValidationConfig(props: OccContextPropsApi): NestedPartial<OccFormFieldValidationConfig> {
    return {
        password: {
            type: FieldFormatType.Password,
            required: true,
            maxLength: 64,
            rules: [
                {
                    displayText: props.passwordRuleCheckerUppercaseRule,
                    regExpression: '([A-Z])',
                },
                {
                    displayText: props.passwordRuleCheckerLowercaseRule,
                    regExpression: '([a-z])',
                },
                {
                    displayText: props.passwordRuleCheckerNumericRule,
                    regExpression: '([0-9])',
                },
                {
                    displayText: props.passwordRuleCheckerSpecialCharacterRule,
                    regExpression: '[\'*.!@#$%^&(){}:;<>,.?/~_|"`¬£]',
                },
                {
                    displayText: props.passwordRuleCheckerMinLengthRule,
                    regExpression: '.{10,}',
                },
            ],
        },
    }
}

export function getFilmConfig(props: OccContextPropsApi): OccFilmConfig {
    return {
        customActions: {},
        showWatchlistButtons: props.watchlistEnabled,
    }
}

export function getFilterConfig(props: OccContextPropsApi): OccFilterConfig {
    return {
        attributeIds: undefined,
        showFullAttributeName: false,
        filmCategories: [FilmCategory.NowShowing, FilmCategory.ComingSoon, FilmCategory.AdvanceBooking],
    }
}

export function getMemberConfig(props: OccContextPropsApi): OccMemberConfig {
    return {
        ...defaultOccMemberConfig,
    }
}

// ASSETS
export function getAssets(props: OccContextPropsApi): OccAssets {
    return {
        ...defaultOccAssets,
        componentErrorIconName: props.componentErrorIcon,
        filmWatchlistButtonIconName: props.watchlistButtonIconName,
        loadingIconName: props.loadingIconName,
        modalCloseButtonIconName: props.modalCloseButtonIcon,

        passwordInputHiddenIconName: props.passwordInputHiddenIcon,
        passwordInputVisibleIconName: props.passwordInputVisibleIcon,
        passwordRuleCheckerInitialIconName: props.passwordRuleCheckerInitialIconName,
        passwordRuleCheckerInvalidIconName: props.passwordRuleCheckerInvalidIconName,
        passwordRuleCheckerSuccessIconName: props.passwordRuleCheckerSuccessIconName,
        advanceBookingPeriodNotificationIconName: props.advanceBookingPeriodNotificationIconName
    }
}

// ACCESSIBILITY
export function getOccAccessibilityText(props: OccContextPropsApi): OccAccessibilityText {
    return {
        ...defaultOccAccessibilityText,

        itemImageAltTextFormat: props.itemImageAltTextFormat,
        addToAppleWalletButtonAriaLabelFormat: props.addToAppleWalletButtonAriaLabelFormat,

        eventGalleryImageAltTextFormat: props.eventGalleryImageAltTextFormat,
        eventPosterAltTextFormat: props.eventPosterAltTextFormat,
        filmAdvertisementAltTextFormat: props.filmAdvertisementAltTextFormat,
        filmBackdropAltTextFormat: props.filmBackdropAltTextFormat,
        filmPosterAltTextFormat: props.filmPosterAltTextFormat,
        filmStillAltTextFormat: props.filmStillAltTextFormat,
        filmTitleTreatmentAltTextFormat: props.filmTitleTreatmentAltTextFormat,
        memberPhotoAltTextFormat: props.memberPhotoAltTextFormat,
        memberPhotoWithoutNameAltTextFormat: props.memberPhotoWithoutNameAltTextFormat,
        memberRewardImageAltTextFormat: props.memberRewardImageAltTextFormat,
        modalCloseButtonAriaLabel: props.modalCloseButtonAriaLabel,
        redirectPaymentMethodButtonAriaLabelFormat: props.redirectPaymentMethodButtonAriaLabelFormat,
        redirectPaymentMethodIconAltTextFormat: props.redirectPaymentMethodIconAltTextFormat,
        removeGiftCardButtonAriaLabelFormat: props.removeGiftCardButtonAriaLabelFormat,
        removeMemberBalancePaymentButtonAriaLabelFormat: props.removeMemberBalancePaymentButtonAriaLabelFormat,
        saveToGooglePayAriaLabelFormat: props.saveToGooglePayAriaLabelFormat,

        // Seat picker
        companionSeatAriaLabelFormat: props.companionSeatAriaLabelFormat,
        sofaLeftSeatAriaLabelFormat: props.sofaLeftSeatAriaLabelFormat,
        sofaMiddleSeatAriaLabelFormat: props.sofaMiddleSeatAriaLabelFormat,
        sofaRightSeatAriaLabelFormat: props.sofaRightSeatAriaLabelFormat,
        unavailableSeatAriaLabelFormat: props.unavailableSeatAriaLabelFormat,
        wheelchairSeatAriaLabelFormat: props.wheelchairSeatAriaLabelFormat,
        normalSeatAriaLabelFormat: props.normalSeatAriaLabelFormat,
    }
}
